import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XMarkIcon, PlusIcon, TrashIcon, UserIcon, BuildingOfficeIcon, CloudArrowUpIcon, CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';
import { createTreatmentPlan, updateTreatmentPlan, autosaveTreatmentPlan } from "../../store/thunks/treatmentPlanThunk";
import { resetLoadingStates, clearAutosaveState, setAutosaveId } from "../../store/slices/treatmentPlanSlice";
import { fetchClinics } from "../../store/thunks/clinicThunk";
import SearchDropdown from "./SearchDropdown";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Autosave interval in milliseconds (10 seconds)
const AUTOSAVE_INTERVAL = 10000;

const TreatmentPlanForm = ({
  onClose,
  onSave,
  editingTreatmentPlan = null,
  clientId = null,
  clientName = null
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { createLoading, updateLoading, autosaveId, autosaveStatus, lastAutosaved } = useSelector((state) => state.treatmentPlan);
  const { clinics } = useSelector((state) => state.clinics);

  const isEditing = !!editingTreatmentPlan;

  // Autosave refs
  const autosaveTimerRef = useRef(null);
  const formDataRef = useRef(null);
  const hasChangesRef = useRef(false);

  // Reset loading states and fetch clinics when form opens
  useEffect(() => {
    console.log(`[Treatment Plan Form] Component mounted, resetting loading states`);
    dispatch(resetLoadingStates());
    dispatch(clearAutosaveState());
    dispatch(fetchClinics());

    // If editing an existing treatment plan, set the autosave ID
    if (editingTreatmentPlan?._id) {
      dispatch(setAutosaveId(editingTreatmentPlan._id));
    }

    // Cleanup on unmount
    return () => {
      if (autosaveTimerRef.current) {
        clearInterval(autosaveTimerRef.current);
      }
      dispatch(clearAutosaveState());
    };
  }, [dispatch, editingTreatmentPlan]);

  const [formData, setFormData] = useState({
    clinicId: user?.clinicId || "",
    clinicName: "",
    clinicAddress: "",
    clinicPhone: "",
    clinicEmail: "",
    clientId: clientId || "",
    clientName: clientName || "",
    date: new Date().toISOString().split('T')[0],
    dateOfBirth: "",
    chronologicalAge: "",
    speechLanguagePathologist: "",
    speechLanguagePathologistId: "",
    speechLanguagePathologistQualifications: "",
    grade: "",
    school: "",
    clinicians: "",
    clinicianId: "",
    currentCommunicationStatus: "",
    treatmentPlan: "",
    goals: [
      {
        id: 1,
        description: ""
      }
    ]
  });

  const [selectedClinic, setSelectedClinic] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [initialSearchValue, setInitialSearchValue] = useState(clientName || "");
  const [selectedSLP, setSelectedSLP] = useState(null);
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [slpSearchValue, setSLPSearchValue] = useState("");
  const [clinicianSearchValue, setClinicianSearchValue] = useState("");

  // Calculate chronological age from date of birth
  const calculateChronologicalAge = (dateOfBirth) => {
    if (!dateOfBirth) return "";

    const dob = new Date(dateOfBirth);
    const today = new Date();

    let years = today.getFullYear() - dob.getFullYear();
    let months = today.getMonth() - dob.getMonth();

    // Adjust if the birthday hasn't occurred yet this year
    if (months < 0 || (months === 0 && today.getDate() < dob.getDate())) {
      years--;
      months += 12;
    }

    // Adjust months if we haven't reached the birth day yet this month
    if (today.getDate() < dob.getDate()) {
      months--;
    }

    // Format the age
    if (years === 0) {
      return `${months} month${months !== 1 ? 's' : ''}`;
    } else if (months === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    } else {
      return `${years} year${years !== 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''}`;
    }
  };

  // Auto-calculate chronological age when date of birth changes
  useEffect(() => {
    if (formData.dateOfBirth) {
      const calculatedAge = calculateChronologicalAge(formData.dateOfBirth);
      setFormData(prev => ({
        ...prev,
        chronologicalAge: calculatedAge
      }));
    }
  }, [formData.dateOfBirth]);

  // Initialize form data when editing
  useEffect(() => {
    if (isEditing && editingTreatmentPlan) {
      console.log(`[Treatment Plan Form] Initializing form for editing:`, editingTreatmentPlan);

      // Convert date to YYYY-MM-DD format for input
      const formattedDate = editingTreatmentPlan.date
        ? new Date(editingTreatmentPlan.date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];

      const formattedDateOfBirth = editingTreatmentPlan.dateOfBirth
        ? new Date(editingTreatmentPlan.dateOfBirth).toISOString().split('T')[0]
        : "";

      setFormData({
        clientId: editingTreatmentPlan.clientId?._id || "",
        clientName: editingTreatmentPlan.clientName || "",
        date: formattedDate,
        dateOfBirth: formattedDateOfBirth,
        chronologicalAge: editingTreatmentPlan.chronologicalAge || "",
        speechLanguagePathologist: editingTreatmentPlan.speechLanguagePathologist || "",
        speechLanguagePathologistId: editingTreatmentPlan.speechLanguagePathologistId?._id || editingTreatmentPlan.speechLanguagePathologistId || "",
        speechLanguagePathologistQualifications: editingTreatmentPlan.speechLanguagePathologistQualifications || "",
        grade: editingTreatmentPlan.grade || "",
        school: editingTreatmentPlan.school || "",
        clinicians: editingTreatmentPlan.clinicians || "",
        clinicianId: editingTreatmentPlan.clinicianId?._id || editingTreatmentPlan.clinicianId || "",
        currentCommunicationStatus: editingTreatmentPlan.currentCommunicationStatus || "",
        treatmentPlan: editingTreatmentPlan.treatmentPlan || "",
        goals: editingTreatmentPlan.goals?.length > 0 ? editingTreatmentPlan.goals : [
          {
            id: 1,
            description: ""
          }
        ]
      });

      // Set initial search value if client is selected
      if (editingTreatmentPlan.clientName) {
        setInitialSearchValue(editingTreatmentPlan.clientName);
        if (editingTreatmentPlan.clientId) {
          setSelectedClient({
            _id: editingTreatmentPlan.clientId._id || editingTreatmentPlan.clientId,
            firstName: editingTreatmentPlan.clientName.split(' ')[0] || '',
            lastName: editingTreatmentPlan.clientName.split(' ').slice(1).join(' ') || '',
          });
        }
      }

      // Set speech-language pathologist search value
      if (editingTreatmentPlan.speechLanguagePathologist) {
        setSLPSearchValue(editingTreatmentPlan.speechLanguagePathologist);
      }

      // Set clinician search value
      if (editingTreatmentPlan.clinicians) {
        setClinicianSearchValue(editingTreatmentPlan.clinicians);
      }
    } else if (clientId && clientName) {
      // Pre-fill client if provided via props
      setInitialSearchValue(clientName);
      setFormData(prev => ({
        ...prev,
        clientId: clientId,
        clientName: clientName
      }));
    }
  }, [isEditing, editingTreatmentPlan, clientId, clientName]);

  const handleClinicSelect = (e) => {
    const clinicId = e.target.value;
    console.log(`[Treatment Plan Form] Selected clinic ID:`, clinicId);

    const clinic = clinics.find(c => c._id === clinicId);
    if (clinic) {
      setSelectedClinic(clinic);
      const fullAddress = `${clinic.address.street}, ${clinic.address.city}, ${clinic.address.state} ${clinic.address.zipCode}`;
      setFormData(prev => ({
        ...prev,
        clinicId: clinic._id,
        clinicName: clinic.name,
        clinicAddress: fullAddress,
        clinicPhone: clinic.phoneNumber,
        clinicEmail: clinic.email
      }));
    }
  };

  const handleClientSelect = (client) => {
    console.log(`[Treatment Plan Form] Selected client:`, client);
    setSelectedClient(client);
    setFormData(prev => ({
      ...prev,
      clientId: client._id,
      clientName: `${client.firstName} ${client.lastName}`,
      dateOfBirth: client.dateOfBirth ? new Date(client.dateOfBirth).toISOString().split('T')[0] : ""
    }));
  };

  const handleSLPSelect = (clinician) => {
    console.log(`[Treatment Plan Form] Selected speech-language pathologist:`, clinician);
    setSelectedSLP(clinician);
    const fullName = `${clinician.firstName} ${clinician.lastName}`;
    setFormData(prev => ({
      ...prev,
      speechLanguagePathologist: fullName,
      speechLanguagePathologistId: clinician._id,
      speechLanguagePathologistQualifications: clinician.invoiceDescriptor || ""
    }));
  };

  const handleClinicianSelect = (clinician) => {
    console.log(`[Treatment Plan Form] Selected clinician:`, clinician);
    setSelectedClinician(clinician);
    const fullName = `${clinician.firstName} ${clinician.lastName}`;
    setFormData(prev => ({
      ...prev,
      clinicians: fullName,
      clinicianId: clinician._id
    }));
  };

  const handleInputChange = (field, value) => {
    console.log(`[Treatment Plan Form] Updating ${field} with value:`, value);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGoalChange = (goalId, field, value) => {
    console.log(`[Treatment Plan Form] Updating goal ${goalId} ${field} with value:`, value);
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.map(goal =>
        goal.id === goalId
          ? { ...goal, [field]: value }
          : goal
      )
    }));
  };

  const addGoal = () => {
    console.log(`[Treatment Plan Form] Adding new goal`);

    // Find the highest id in existing goals and increment by 1
    const maxId = formData.goals.reduce((max, goal) => Math.max(max, goal.id || 0), 0);
    const newGoal = {
      id: maxId + 1,
      description: ""
    };

    setFormData(prev => ({
      ...prev,
      goals: [...prev.goals, newGoal]
    }));
  };

  const removeGoal = (goalId) => {
    console.log(`[Treatment Plan Form] Removing goal ${goalId}`);
    if (formData.goals.length > 1) {
      setFormData(prev => ({
        ...prev,
        goals: prev.goals.filter(goal => goal.id !== goalId)
      }));
    } else {
      toast.warning("At least one goal is required.");
    }
  };

  // Autosave function
  const performAutosave = useCallback(async () => {
    // Only autosave if there are changes and we have minimal data
    if (!hasChangesRef.current) {
      console.log(`[Treatment Plan Form] Autosave skipped - no changes`);
      return;
    }

    const currentFormData = formDataRef.current;
    if (!currentFormData) return;

    // Only autosave if user has started entering data
    const hasMinimalData = currentFormData.clientName ||
                           currentFormData.currentCommunicationStatus ||
                           currentFormData.treatmentPlan ||
                           currentFormData.goals.some(g => g.description);

    if (!hasMinimalData) {
      console.log(`[Treatment Plan Form] Autosave skipped - insufficient data`);
      return;
    }

    console.log(`[Treatment Plan Form] Performing autosave...`);

    try {
      await dispatch(autosaveTreatmentPlan({
        treatmentPlanId: autosaveId || (isEditing ? editingTreatmentPlan._id : null),
        treatmentPlanData: currentFormData
      })).unwrap();

      hasChangesRef.current = false;
      console.log(`[Treatment Plan Form] Autosave successful`);
    } catch (error) {
      console.error(`[Treatment Plan Form] Autosave failed:`, error);
      // Silent failure - don't show error toast for autosave
    }
  }, [dispatch, autosaveId, isEditing, editingTreatmentPlan]);

  // Keep formDataRef in sync with formData
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Set up autosave timer
  useEffect(() => {
    // Start autosave timer
    autosaveTimerRef.current = setInterval(() => {
      performAutosave();
    }, AUTOSAVE_INTERVAL);

    return () => {
      if (autosaveTimerRef.current) {
        clearInterval(autosaveTimerRef.current);
      }
    };
  }, [performAutosave]);

  // Mark changes when form data changes
  useEffect(() => {
    hasChangesRef.current = true;
  }, [formData]);

  // Helper function to format last autosaved time
  const formatAutosaveTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleSave = async () => {
    console.log(`[Treatment Plan Form] Saving form data:`, formData);

    // Basic validation
    if (!selectedClient || !formData.clientId) {
      toast.error("Please select a client from the search dropdown.");
      return;
    }

    if (!formData.clientName.trim()) {
      toast.error("Client name is required.");
      return;
    }

    if (!formData.date) {
      toast.error("Date is required.");
      return;
    }

    // Check if at least one goal has a description
    const hasValidGoal = formData.goals.some(goal => goal.description.trim());
    if (!hasValidGoal) {
      toast.error("At least one goal description is required.");
      return;
    }

    try {
      // Stop autosave timer before saving
      if (autosaveTimerRef.current) {
        clearInterval(autosaveTimerRef.current);
      }

      let result;

      // Add status: 'complete' to mark this as a final save (not a draft)
      const saveData = { ...formData, status: 'complete' };

      if (isEditing || autosaveId) {
        // Update existing treatment plan (either editing or updating a draft)
        const planId = isEditing ? editingTreatmentPlan._id : autosaveId;
        result = await dispatch(updateTreatmentPlan({
          treatmentPlanId: planId,
          treatmentPlanData: saveData
        })).unwrap();
        toast.success("Treatment Plan saved successfully.");
      } else {
        // Create new treatment plan
        result = await dispatch(createTreatmentPlan(saveData)).unwrap();
        toast.success("Treatment Plan created successfully.");
      }

      // Call the save function passed from parent (if provided)
      if (onSave) {
        onSave(result);
      }

      onClose();
    } catch (error) {
      console.error(`[Treatment Plan Form] Error saving:`, error);
      const errorMessage = error?.message || `Failed to ${isEditing ? 'update' : 'create'} treatment plan`;
      toast.error(errorMessage);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-70 overflow-y-auto">
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-sm shadow-xl max-w-6xl w-full mx-4 max-h-[95vh] overflow-y-auto">
        <div className="absolute top-0 right-0 pt-4 pr-4 z-10">
          <button
            onClick={onClose}
            className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Autosave Status Indicator */}
          <div className="absolute top-4 left-4 flex items-center text-xs">
            {autosaveStatus === 'saving' && (
              <span className="flex items-center text-blue-600">
                <CloudArrowUpIcon className="h-4 w-4 mr-1 animate-pulse" />
                Saving draft...
              </span>
            )}
            {autosaveStatus === 'saved' && lastAutosaved && (
              <span className="flex items-center text-green-600">
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                Draft saved at {formatAutosaveTime(lastAutosaved)}
              </span>
            )}
            {autosaveStatus === 'error' && (
              <span className="flex items-center text-amber-600">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                Autosave failed
              </span>
            )}
          </div>

          {/* Header Section */}
          <div className="border-b border-gray-200 pb-6 mb-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Speech & Language Treatment Plan
              </h2>
              {formData.clinicAddress ? (
                <>
                  <p className="text-sm text-gray-600">{formData.clinicAddress}</p>
                  <p className="text-sm text-gray-600">
                    Tel: {formData.clinicPhone || 'N/A'} • e-mail: {formData.clinicEmail || 'N/A'}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    44 Main St. E., Milton, ON L9T 1N3 • Tel: (905) 875-3345 • Fax: (905) 875-0343
                  </p>
                  <p className="text-sm text-gray-600">e-mail: milton@hearsay.ca</p>
                </>
              )}
            </div>

            {/* Clinic Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Clinic: <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.clinicId}
                onChange={handleClinicSelect}
                className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                <option value="">-- Select a clinic --</option>
                {clinics?.map((clinic) => (
                  <option key={clinic._id} value={clinic._id}>
                    {clinic.name} - {clinic.address.city}
                  </option>
                ))}
              </select>

              {/* Show selected clinic info */}
              {formData.clinicId && selectedClinic && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center text-sm text-blue-800">
                    <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                    <span className="font-medium">
                      {formData.clinicName} - {formData.clinicAddress}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Basic Information Grid */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Name: <span className="text-red-500">*</span>
                </label>
                <SearchDropdown
                  key="client-search-dropdown"
                  displayFields={["firstName", "lastName"]}
                  onSelection={handleClientSelect}
                  prefix=":client:"
                  placeholder="Search for client by name..."
                  initialSelection={selectedClient}
                  initialValue={initialSearchValue}
                  separator=" "
                />

                {/* Show selected client info */}
                {formData.clientId && selectedClient && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center text-sm text-green-800">
                      <UserIcon className="h-4 w-4 mr-1" />
                      <span className="font-medium">Linked to existing client: {formData.clientName}</span>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date: <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  D.O.B.:
                </label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  C.A. (Chronological Age):
                  <span className="ml-2 text-xs text-gray-500 font-normal italic">
                    (Auto-calculated from DOB)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.chronologicalAge}
                  readOnly
                  className="block w-full rounded-sm border-gray-300 bg-gray-50 shadow-sm sm:text-sm cursor-not-allowed"
                  placeholder="Select date of birth to calculate age"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Speech-Language Pathologist:
                </label>
                <SearchDropdown
                  key="slp-search-dropdown"
                  displayFields={["firstName", "lastName"]}
                  onSelection={handleSLPSelect}
                  prefix=":clinician:"
                  placeholder="Search for SLP..."
                  initialSelection={selectedSLP}
                  initialValue={slpSearchValue}
                  separator=" "
                />

                {/* Show selected SLP info */}
                {formData.speechLanguagePathologistId && selectedSLP && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center text-sm text-blue-800">
                      <UserIcon className="h-4 w-4 mr-1" />
                      <span className="font-medium">Linked to clinician: {formData.speechLanguagePathologist}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grade:
                </label>
                <input
                  type="text"
                  value={formData.grade}
                  onChange={(e) => handleInputChange('grade', e.target.value)}
                  className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Enter grade"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School:
                </label>
                <input
                  type="text"
                  value={formData.school}
                  onChange={(e) => handleInputChange('school', e.target.value)}
                  className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Enter school name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clinician(s):
                </label>
                <SearchDropdown
                  key="clinician-search-dropdown"
                  displayFields={["firstName", "lastName"]}
                  onSelection={handleClinicianSelect}
                  prefix=":clinician:"
                  placeholder="Search for clinician..."
                  initialSelection={selectedClinician}
                  initialValue={clinicianSearchValue}
                  separator=" "
                />

                {/* Show selected clinician info */}
                {formData.clinicianId && selectedClinician && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center text-sm text-blue-800">
                      <UserIcon className="h-4 w-4 mr-1" />
                      <span className="font-medium">Linked to clinician: {formData.clinicians}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Current Communication Status */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Communication Status:
            </label>
            <ReactQuill
              value={formData.currentCommunicationStatus}
              onChange={(content) => handleInputChange('currentCommunicationStatus', content)}
              theme="snow"
              placeholder="Describe the client's current communication status..."
            />
          </div>

          {/* Treatment Plan Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Treatment Plan:
            </label>
            <ReactQuill
              value={formData.treatmentPlan}
              onChange={(content) => handleInputChange('treatmentPlan', content)}
              theme="snow"
              placeholder="Describe the treatment plan..."
            />
          </div>

          {/* Goals Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Goals
              </h3>
              <button
                onClick={addGoal}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Goal
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                      Goal Description
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Remove
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {formData.goals.map((goal, index) => (
                    <tr key={goal.id} className="border-b border-gray-300">
                      <td className="px-4 py-3 border-r border-gray-300 text-center font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-4 py-3 border-r border-gray-300">
                        <ReactQuill
                          value={goal.description}
                          onChange={(content) => handleGoalChange(goal.id, 'description', content)}
                          theme="snow"
                          placeholder={`Enter goal ${index + 1} description...`}
                        />
                      </td>
                      <td className="px-2 py-3 text-center">
                        {formData.goals.length > 1 && (
                          <button
                            onClick={() => removeGoal(goal.id)}
                            className="text-red-600 hover:text-red-900">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer Signature Section */}
          <div className="border-t border-gray-200 pt-6 mb-6">
            <div className="text-center text-sm text-gray-600">
              {formData.speechLanguagePathologist && (
                <p className="font-medium">{formData.speechLanguagePathologist}</p>
              )}
              {!formData.speechLanguagePathologist && (
                <p className="text-gray-400 italic">Speech-Language Pathologist will appear here when selected</p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={createLoading || updateLoading}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                createLoading || updateLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}>
              {createLoading || updateLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isEditing ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                isEditing ? 'Update Treatment Plan' : 'Save Treatment Plan'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentPlanForm;
